import { Service } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy } from '@angular/router';
import { ACCOUNT_ROUTES_PATHS_PARTIALS } from '@features/account/account-routes-paths';
import { VENUE_ROUTES_PATHS_PARTIALS } from '@features/venue/venue-routes-paths';
import { CONSTANTS } from '@shared/constants';

/**
 * The two routes that introduce a context. They render the shell, which follows the context on
 * its own, so they are kept and only the page underneath them is rebuilt.
 */
const CONTEXT_ROOT_PATHS: ReadonlyArray<string> = [
  ACCOUNT_ROUTES_PATHS_PARTIALS.BASE_PATH,
  VENUE_ROUTES_PATHS_PARTIALS.BASE_PATH,
];

/**
 * Account first: a page about one venue can sit inside an account context
 * (`.../settings/venues/edit/:venueId`) and carry both parameters. The account is the context it
 * belongs to; the venue is just what the page is about.
 */
const CONTEXT_PARAMS: ReadonlyArray<string> = [
  CONSTANTS.ROUTE_PARAMS.ACCOUNT_ID,
  CONSTANTS.ROUTE_PARAMS.VENUE_ID,
];

function contextUuidOf(route: ActivatedRouteSnapshot): string | null {
  for (const param of CONTEXT_PARAMS) {
    const uuid = route.paramMap.get(param);

    if (uuid) {
      return uuid;
    }
  }

  return null;
}

function isContextRoot(route: ActivatedRouteSnapshot): boolean {
  return CONTEXT_ROOT_PATHS.includes(route.routeConfig?.path ?? '');
}

/**
 * Rebuilds the page when the context changes underneath it.
 *
 * Switching account or venue while staying on the same page only changes the `:accountId` /
 * `:venueId` segment. Angular's default strategy reuses a component whenever the route config
 * matches, so nothing was re-initialised and the page kept showing the previous context's data.
 * Pages read their context once, when they are created — an idiom the old switcher hid by always
 * landing on the context's scoreboard, which was a different route and therefore a new component.
 *
 * Treating a changed context uuid as a different route restores that guarantee without asking
 * every page to subscribe to context changes. Parameter changes within one context — drilling
 * into a category, opening another campaign — still reuse, as before.
 */
@Service({ autoProvided: false })
export class ContextRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (!super.shouldReuseRoute(future, curr)) {
      return false;
    }

    if (isContextRoot(future)) {
      return true;
    }

    return contextUuidOf(future) === contextUuidOf(curr);
  }
}
